import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'

interface NotificationSettings {
  id: number
  enabled: boolean
  recipients: string[]
  digestHour: number
  minSeverity: 'high' | 'medium' | 'low'
  includeShopify: boolean
  includeContracts: boolean
  includeLab: boolean
  includeTasks: boolean
  includeMeetings: boolean
  lastSentAt: string | null
  lastSentCount: number | null
  updatedAt: string
}

export function SetariNotificariPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  // Destinatarii primesc toate alertele firmei: ii schimba doar adminul (API-ul cere la fel).
  const canWrite = permissions.includes('alerts:write') && isAdmin

  const query = useQuery<{ settings: NotificationSettings | null }>({
    queryKey: ['notification-settings'],
    queryFn: async () =>
      (await api.get<{ settings: NotificationSettings | null }>('/alerts/notification-settings'))
        .data,
  })

  const [enabled, setEnabled] = useState(false)
  const [recipientsText, setRecipientsText] = useState('')
  const [digestHour, setDigestHour] = useState(8)
  const [minSeverity, setMinSeverity] = useState<'high' | 'medium' | 'low'>('medium')
  const [includeShopify, setIncludeShopify] = useState(true)
  const [includeContracts, setIncludeContracts] = useState(true)
  const [includeLab, setIncludeLab] = useState(true)
  const [includeTasks, setIncludeTasks] = useState(true)
  const [includeMeetings, setIncludeMeetings] = useState(false)

  useEffect(() => {
    const s = query.data?.settings
    if (s) {
      setEnabled(s.enabled)
      setRecipientsText(s.recipients.join(', '))
      setDigestHour(s.digestHour)
      setMinSeverity(s.minSeverity)
      setIncludeShopify(s.includeShopify)
      setIncludeContracts(s.includeContracts)
      setIncludeLab(s.includeLab)
      setIncludeTasks(s.includeTasks)
      setIncludeMeetings(s.includeMeetings)
    }
  }, [query.data])

  const save = useMutation({
    mutationFn: async () => {
      const recipients = recipientsText
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      return (
        await api.put<{ settings: NotificationSettings }>('/alerts/notification-settings', {
          enabled,
          recipients,
          digestHour,
          minSeverity,
          includeShopify,
          includeContracts,
          includeLab,
          includeTasks,
          includeMeetings,
        })
      ).data
    },
    onSuccess: () => {
      toast.success('Setări salvate')
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] })
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la salvare'
      toast.error(message)
    },
  })

  const testDigest = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          sent: boolean
          alertCount: number
          recipients: string[]
          reason?: string
        }>('/alerts/test-digest')
      ).data,
    onSuccess: (d) => {
      if (d.sent) {
        toast.success(
          `Rezumat trimis către ${d.recipients.length} destinatar(i) (${d.alertCount} alerte)`,
        )
        queryClient.invalidateQueries({ queryKey: ['notification-settings'] })
      } else {
        toast.warning(d.reason ?? 'Rezumatul nu a fost trimis')
      }
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la trimitere test'
      toast.error(message)
    },
  })

  const s = query.data?.settings
  const lastSent = s?.lastSentAt ? new Date(s.lastSentAt) : null

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → Notificări email</h1>

      <Card
        title="Rezumat zilnic al alertelor"
        description="Email automat trimis zilnic către destinatarii configurați, cu sumar alerte active"
      >
        {s && (
          <div className="mb-4 rounded border border-b4l-blue-light bg-b4l-blue-light/30 p-3 text-xs">
            <div>
              <strong>Stare:</strong>{' '}
              {s.enabled ? (
                <span className="text-b4l-green-dark">✓ Activ</span>
              ) : (
                <span className="text-b4l-red-dark">✗ Dezactivat</span>
              )}
            </div>
            <div>
              <strong>Destinatari:</strong>{' '}
              {s.recipients.length > 0 ? s.recipients.join(', ') : 'niciunul'}
            </div>
            <div>
              <strong>Trimis zilnic la ora:</strong> {s.digestHour}:00 (ora României)
            </div>
            <div>
              <strong>Ultimul rezumat:</strong>{' '}
              {lastSent
                ? `${lastSent.toLocaleString('en-GB')} (${s.lastSentCount ?? 0} alerte)`
                : 'niciodată'}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!canWrite}
            />
            <span className="font-medium">Activează rezumatul zilnic</span>
          </label>

          <Field
            label="Destinatari (separați prin virgulă)"
            placeholder="alex@boost4life.ro, contabilitate@boost4life.ro"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            disabled={!canWrite}
          />

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-b4l-text-dim">Ora trimitere (0-23)</label>
              <input
                type="number"
                min={0}
                max={23}
                value={digestHour}
                onChange={(e) => setDigestHour(Math.max(0, Math.min(23, Number(e.target.value))))}
                disabled={!canWrite}
                className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-b4l-text-dim">Severitate minimă</label>
              <select
                value={minSeverity}
                onChange={(e) => setMinSeverity(e.target.value as typeof minSeverity)}
                disabled={!canWrite}
                className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
              >
                <option value="high">Doar critice</option>
                <option value="medium">Critice + importante</option>
                <option value="low">Toate</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-b4l-text-dim">
              Tipuri de alerte incluse
            </label>
            <div className="mt-2 grid grid-cols-1 gap-1 md:grid-cols-2">
              <Toggle
                label="Clienți Shopify (VIP/recurrent)"
                value={includeShopify}
                onChange={setIncludeShopify}
                disabled={!canWrite}
              />
              <Toggle
                label="Contracte care expiră"
                value={includeContracts}
                onChange={setIncludeContracts}
                disabled={!canWrite}
              />
              <Toggle
                label="Loturi laborator care expiră"
                value={includeLab}
                onChange={setIncludeLab}
                disabled={!canWrite}
              />
              <Toggle
                label="Task-uri restante"
                value={includeTasks}
                onChange={setIncludeTasks}
                disabled={!canWrite}
              />
              <Toggle
                label="Întâlniri viitoare (7z)"
                value={includeMeetings}
                onChange={setIncludeMeetings}
                disabled={!canWrite}
              />
            </div>
          </div>

          {canWrite && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                Salvează
              </Button>
              <Button
                variant="secondary"
                onClick={() => testDigest.mutate()}
                loading={testDigest.isPending}
              >
                Trimite test acum
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card description="Pentru ca emailul să fie trimis, trebuie să ai SMTP configurat în Setări → SMTP/IMAP. Verificarea rulează la fiecare oră fixă, deci modificările au efect imediat.">
        <p className="text-xs text-b4l-text-muted">
          <strong>Notă:</strong> ora de trimitere e ora României. Rezumatul se trimite o singură
          dată pe zi, fără duplicate.
        </p>
      </Card>
    </div>
  )
}

function Toggle({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded border border-b4l-border bg-white px-2 py-1.5 text-sm">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  )
}
